import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import type { VideoEmbed as VideoEmbedType } from '@/hooks/useLinkPreview';

interface VideoEmbedProps {
  embed: VideoEmbedType;
  originalUrl: string;
  title?: string;
  className?: string;
}

export function VideoEmbed({ embed, originalUrl, title, className }: VideoEmbedProps) {
  const [hasError, setHasError] = useState(false);

  // Validate that the embed URL matches expected patterns
  const isValidEmbedUrl = (() => {
    try {
      const url = new URL(embed.embedUrl);
      const allowedDomains = [
        'www.youtube.com',
        'player.vimeo.com',
        'player.twitch.tv',
        'www.tiktok.com',
        'www.dailymotion.com'
      ];
      return allowedDomains.includes(url.hostname);
    } catch {
      return false;
    }
  })();

  const handleVisitOriginal = () => {
    window.open(originalUrl, '_blank', 'noopener,noreferrer');
  };

  if (hasError || !isValidEmbedUrl) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 min-w-0">
              <div className="h-12 w-12 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <ExternalLink className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="min-w-0">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                  {title || `${embed.provider} Video`}
                </h3>
                <p className="text-sm text-red-600 dark:text-red-400">
                  Unable to load video embed
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleVisitOriginal}
              className="flex-shrink-0 ml-4"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Watch on {embed.provider}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardContent className="p-0">
        <div
          className="relative w-full overflow-hidden rounded-t-lg"
          style={{ aspectRatio: embed.aspectRatio || '16/9' }}
        >
          <iframe
            src={embed.embedUrl}
            title={title || `${embed.provider} Video`}
            className="absolute inset-0 w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="strict-origin-when-cross-origin"
            loading="lazy"
            onError={() => setHasError(true)}
          />
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between min-w-0">
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 min-w-0 flex-1">
              <span className="font-medium flex-shrink-0">{embed.provider}</span>
              {title && (
                <>
                  <span className="flex-shrink-0">•</span>
                  <span className="truncate min-w-0">{title}</span>
                </>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleVisitOriginal}
              className="flex-shrink-0 ml-4"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Original
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}